<script setup lang="ts">
// Vue Core
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Validation
import useVuelidate from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'

// Stores
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/public/stores/workspace-store'

// Layout & Components
import AuthLayout from '@/modules/auth/layouts/auth-layout.vue'
import { VButton, VTextField } from '@/modules/shared/components'

// -----------------------------------------------------------------------------
// Route, Stores, and State
// -----------------------------------------------------------------------------

const router = useRouter()
const route = useRoute()

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

const slug = route.params.slug as string
const registrationType = route.query.type as 'company' | 'investor'
const workspace = computed(() => workspaceStore.workspace)

// -----------------------------------------------------------------------------
// Form State & Validation
// -----------------------------------------------------------------------------

const form = ref({
  email: null,
})

const loading = ref(false)
const isEmailVerificationSuccess = ref<null | boolean>(null)

const rules = {
  email: { required, email },
}

const v$ = useVuelidate(rules, form, { $lazy: true })

// -----------------------------------------------------------------------------
// Actions
// -----------------------------------------------------------------------------

const requestEmailVerification = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  loading.value = true

  const payload = {
    email: form.value.email,
    registration_type: registrationType,
    slug,
  }

  const { error } = await authStore.requestEmailVerification(payload)

  if (error) {
    isEmailVerificationSuccess.value = false
  } else {
    router.push({ name: 'email-verification-instructions' })
  }

  loading.value = false
}

// -----------------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------------

onMounted(async () => {
  await workspaceStore.fetchWorkspace(slug)

  const options = workspace.value?.options
  if (!options) return

  if (
    (registrationType === 'company' && !options.is_funding_application_enabled) ||
    (registrationType === 'investor' && !options.is_public_investor_registration_enabled)
  ) {
    router.push({ name: 'public.welcome' })
  }
})
</script>

<template>
  <AuthLayout>
    <template #icon>
      <svg width="72" height="96" viewBox="0 0 72 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M63 39H9C5.68629 39 3 41.6863 3 45V87C3 90.3137 5.68629 93 9 93H63C66.3137 93 69 90.3137 69 87V45C69 41.6863 66.3137 39 63 39Z"
          fill="#111827"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15 39V24C15 12.4 24.4 3 36 3C47.56 3 57 12.4 57 24V39"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M36 60V72" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>

    <div class="w-full sm:w-[420px]">
      <header>
        <h1 class="text-3xl leading-9 text-gray-900">Verify Email</h1>
        <p class="mt-6 text-gray-700">Please complete the information below to set up your account.</p>
      </header>

      <template v-if="!isEmailVerificationSuccess">
        <main class="sm:mt-18 mt-12">
          <div v-if="isEmailVerificationSuccess === false" class="mb-10 w-full rounded-lg bg-red-200 p-4">
            Sorry, there was an error verifying your email. Please try again or contact support for assistance.
          </div>

          <form @submit.prevent="requestEmailVerification">
            <VTextField v-model="form.email" label="Email" placeholder="Email" property="email" :v$="v$" />
          </form>

          <VButton class="mt-6 w-full" :loading="loading" size="xl" variant="primary" @click="requestEmailVerification">
            Verify
          </VButton>
        </main>
      </template>

      <div class="mt-12 text-center text-sm">
        Already have an account?
        <RouterLink class="ml-1 font-semibold text-[#3D8A9D]" :to="{ name: 'login', query: { account: slug } }">
          Login
        </RouterLink>
      </div>
    </div>
  </AuthLayout>
</template>
